import { useMutation, useQueryClient } from '@tanstack/react-query';
import { bulkDeleteFiles } from '../api/filesApi';
import { recycleBinKeys } from '../api/recycleBinApi';
import { siteFilesKeys } from './useSiteFiles';
import { folderContentsKeys } from './useFolderContents';
import { useFileSelectionStore } from '../stores/fileSelectionStore';
import { useSnackbarStore } from '../../../shared/stores/snackbarStore';

export function useBulkDelete(siteId: number) {
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbarStore(state => state.showSnackbar);
  const clearAll = useFileSelectionStore(state => state.clearAll);

  return useMutation({
    mutationFn: async ({
      fileIds,
      folderIds,
    }: {
      fileIds: string[];
      folderIds: string[];
    }) => {
      // Batch into chunks of 100 items
      const allFileIds = [...fileIds];
      const allFolderIds = [...folderIds];

      while (allFileIds.length > 0 || allFolderIds.length > 0) {
        const batchFileIds: string[] = [];
        const batchFolderIds: string[] = [];
        let remaining = 100;

        while (remaining > 0 && allFolderIds.length > 0) {
          batchFolderIds.push(allFolderIds.shift()!);
          remaining--;
        }
        while (remaining > 0 && allFileIds.length > 0) {
          batchFileIds.push(allFileIds.shift()!);
          remaining--;
        }

        await bulkDeleteFiles(siteId, batchFileIds, batchFolderIds);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: siteFilesKeys.list(siteId),
      });
      queryClient.invalidateQueries({
        queryKey: folderContentsKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: recycleBinKeys.all,
        refetchType: 'all',
      });
      clearAll();
      showSnackbar('Items deleted', 'success');
    },
  });
}
